import { ref, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { Capacitor } from '@capacitor/core'
import { App } from '@capacitor/app'

/**
 * Composable for handling back button detection
 * Replaces BackButtonMixin functionality using Vue 3 Composition API
 *
 * @param {Function} customHandler - Optional custom handler for back button press
 * @returns {Object} Back button detection functions
 */
export function useBackButton(customHandler) {
  const router = useRouter()

  // State for tracking handlers
  const nativeHandler = ref(null)
  const routeHandler = ref(null)

  /**
   * Set up back button detection on mobile devices and browsers
   */
  const setupBackButtonDetector = async () => {
    // Clean up existing handlers first
    destroyBackButtonDetector()

    // Set up native handler for mobile platforms
    if (Capacitor.isNativePlatform()) {
      nativeHandler.value = await App.addListener('backButton',
        event => onBackButtonPressed('native', event)
      )
    }

    // Route guard to stop web back button
    routeHandler.value = router.beforeEach((to, from, next) => {
      onBackButtonPressed('router', to)
      next(false)
    })
  }

  /**
   * Clean up and remove back button handlers
   */
  const destroyBackButtonDetector = () => {
    if (nativeHandler.value) {
      nativeHandler.value.remove()
      nativeHandler.value = null
    }

    if (routeHandler.value) {
      routeHandler.value()
      routeHandler.value = null
    }
  }

  /**
   * Default handler for back button press
   * Can be overridden by the component using the composable
   *
   * @param {string} source - Source of the back button press ('native' or 'router')
   * @param {Object} data - Additional data related to the event
   */
  const onBackButtonPressed = (source, data) => {
    if (customHandler) {
      customHandler(source, data)
    } else {
      console.warn('No action specified for back button handler')
    }
  }

  // Clean up on component unmount
  onBeforeUnmount(() => {
    destroyBackButtonDetector()
  })

  return {
    setupBackButtonDetector,
    destroyBackButtonDetector,
    onBackButtonPressed
  }
}
